import {
  ClipboardEvent,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react-dom';

import { cx } from '@libs/classnames';
import { Badge, BadgeProps } from '@ui/components/Badge';
import { ContextMenu, ContextMenuItem } from '@ui/components/ContextMenu/ContextMenu';

import styles from './TagListEditor.module.css';

export type Props = {
  value: string[];
  onAdd: (tag: string) => void;
  // Пакетное добавление (вставка нескольких тегов через запятую). Без него
  // вставка падает обратно на onAdd по одному.
  onAddMany?: (tags: string[]) => void;
  onRemove: (tag: string, index: number) => void;
  // Enter по пустому полю (когда нечего коммитить) — «отправить форму».
  onSubmit?: () => void;
  suggestions?: string[];
  onQueryChange?: (query: string) => void;
  placeholder?: string;
  className?: string;
  badgeSize?: BadgeProps['size'];
  disabled?: boolean;
  autoFocus?: boolean;
  // Максимум тегов (при достижении инпут скрывается) и длина одного тега.
  maxTags?: number;
  maxTagLength?: number;
};

export function TagListEditor({
  value,
  onAdd,
  onAddMany,
  onRemove,
  onSubmit,
  suggestions,
  onQueryChange,
  placeholder,
  className,
  badgeSize = 'm',
  disabled,
  autoFocus,
  maxTags,
  maxTagLength,
}: Props) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const filtered = useMemo(() => {
    if (!suggestions || suggestions.length === 0) return [];
    const q = draft.trim().toLowerCase();
    const taken = new Set(value);
    return suggestions.filter((s) => !taken.has(s) && (q === '' || s.toLowerCase().includes(q)));
  }, [suggestions, draft, value]);

  useEffect(() => {
    setHighlight((h) => (h !== null && h < filtered.length ? h : null));
  }, [filtered.length]);

  useEffect(() => {
    if (highlight === null) return;
    optionRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const showDropdown = open && filtered.length > 0;

  const floating = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(6),
      flip(),
      shift({ padding: 8 }),
      size({
        apply: ({ elements, availableHeight }) => {
          // Ширину ограничиваем шириной редактора (а не узкого инпута-референса).
          const editorWidth = rootRef.current?.offsetWidth;
          Object.assign(elements.floating.style, {
            maxWidth: editorWidth ? `${editorWidth}px` : '',
            maxHeight: `${Math.min(220, availableHeight)}px`,
          });
        },
        padding: 8,
      }),
    ],
  });

  // oxlint-disable-next-line eqeqeq
  const full = maxTags != null && value.length >= maxTags;

  // Убираем запятые (разделитель), обрезаем пробелы и длину.
  const sanitize = (raw: string) => {
    const tag = raw.replace(/,/g, '').trim();
    // oxlint-disable-next-line eqeqeq
    return maxTagLength != null ? tag.slice(0, maxTagLength) : tag;
  };

  const reset = () => {
    setDraft('');
    onQueryChange?.('');
    setHighlight(null);
  };

  const commit = (raw: string) => {
    const tag = sanitize(raw);
    if (!tag) return;
    // Дубликат (или упёрлись в лимит) не добавляем, но поле всё равно очищаем —
    // Enter/запятая не должны «зависать» с введённым текстом.
    if (!full && !value.includes(tag)) onAdd(tag);
    reset();
  };

  // Вставка нескольких тегов: делим по запятым, чистим, дедупим, режем по лимиту.
  const addMany = (text: string) => {
    const seen = new Set(value);
    // oxlint-disable-next-line eqeqeq
    const room = maxTags != null ? maxTags - value.length : Infinity;
    const toAdd: string[] = [];
    for (const part of text.split(',')) {
      if (toAdd.length >= room) break;
      const tag = sanitize(part);
      if (tag && !seen.has(tag)) {
        seen.add(tag);
        toAdd.push(tag);
      }
    }
    if (!toAdd.length) return;
    if (onAddMany) onAddMany(toAdd);
    else toAdd.forEach(onAdd);
    reset();
  };

  const handleChange = (next: string) => {
    setDraft(next);
    onQueryChange?.(next);
    setOpen(true);
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    addMany(e.clipboardData.getData('text'));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter') {
      const picked = highlight !== null ? filtered[highlight] : undefined;
      const tag = picked ?? draft;
      if (!tag.trim()) {
        // Нечего коммитить — Enter «отправляет форму» (напр. сохраняет диалог).
        if (onSubmit) {
          e.preventDefault();
          onSubmit();
        }
        return;
      }
      e.preventDefault();
      commit(tag);
      if (picked !== undefined) setOpen(false);
    } else if (e.key === ',') {
      // Запятая коммитит текущий черновик (сам символ не вводим).
      e.preventDefault();
      commit(draft);
    } else if (e.code === 'Backspace' && draft === '' && value.length > 0) {
      const last = value.length - 1;
      onRemove(value[last]!, last);
    } else if (e.code === 'ArrowDown' && filtered.length > 0) {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h === null ? 0 : (h + 1) % filtered.length));
    } else if (e.code === 'ArrowUp' && filtered.length > 0) {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) =>
        h === null ? filtered.length - 1 : (h - 1 + filtered.length) % filtered.length,
      );
    } else if (e.code === 'Escape') {
      setOpen(false);
      setHighlight(null);
    }
  };

  const handleSuggestionMouseDown = (e: MouseEvent, tag: string) => {
    // удерживаем фокус на инпуте
    e.preventDefault();
    commit(tag);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div
      ref={rootRef}
      className={cx(className, styles.root, styles[`size-${badgeSize}`], {
        [styles.disabled]: disabled,
      })}
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((tag, i) => (
        <Badge
          key={`${tag}-${i}`}
          size={badgeSize}
          flat
          onRemove={disabled ? undefined : () => onRemove(tag, i)}
        >
          {tag}
        </Badge>
      ))}
      {!full && (
        <input
          ref={(el) => {
            inputRef.current = el;
            // Дропдаун позиционируем относительно самого инпута, а не всего редактора.
            floating.refs.setReference(el);
          }}
          className={styles.input}
          type="text"
          value={draft}
          autoFocus={autoFocus}
          disabled={disabled}
          maxLength={maxTagLength}
          placeholder={value.length === 0 ? placeholder : undefined}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />
      )}
      {showDropdown &&
        createPortal(
          <ContextMenu
            ref={floating.refs.setFloating}
            className={styles.dropdown}
            style={floating.floatingStyles}
          >
            {filtered.map((tag, i) => (
              <ContextMenuItem
                key={tag}
                ref={(el) => {
                  optionRefs.current[i] = el;
                }}
                highlighted={i === highlight}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => handleSuggestionMouseDown(e, tag)}
              >
                {tag}
              </ContextMenuItem>
            ))}
          </ContextMenu>,
          window.document.body,
        )}
    </div>
  );
}
